/**
 * Links to files stored by crm-api carry the access token in `?token=`
 * (fileUrl). The token lives an hour: a link or an image made after an idle
 * hour would get 401 (final review M-10). So:
 *   * useFileUrl: the link follows the stored session (re-rendered when the
 *     token is refreshed, in this tab or another), and a link made with an
 *     expired token asks for a fresh one first;
 *   * openFile / fileLinkClick: a click on a file link refreshes an expired
 *     token before the file opens.
 */
import type { MouseEvent } from "react";
import { useEffect, useMemo, useSyncExternalStore } from "react";

import { fileUrl, subscribeSession, tokenStore } from "./client";

const FILES_PREFIX = "/api/files/";

const isFileLink = (src: string | null | undefined) => {
  if (!src) return false;
  try {
    const url = new URL(src, location.origin);
    return (
      url.origin === location.origin && url.pathname.startsWith(FILES_PREFIX)
    );
  } catch {
    return false;
  }
};

export function useFileUrl(src: string): string;
export function useFileUrl(src: string | null | undefined): string | undefined;
export function useFileUrl(src: string | null | undefined) {
  const token = useSyncExternalStore(
    subscribeSession,
    tokenStore.peek,
    () => null,
  );
  useEffect(() => {
    // the refresh saves the session, which re-renders this link
    if (isFileLink(src) && tokenStore.needsRefresh()) void tokenStore.get();
  }, [src, token]);
  // token: the link is rebuilt when the session changes
  return useMemo(() => fileUrl(src), [src, token]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Opens a crm-api file in a new tab with a fresh access token. */
export const openFile = async (src: string) => {
  await tokenStore.get();
  window.open(fileUrl(src), "_blank", "noopener,noreferrer");
};

/**
 * onClick of an `<a href={useFileUrl(src)} target="_blank">`: with a fresh
 * token the browser follows the link itself; with an expired one the token
 * is refreshed first. Stops the click from reaching the row around the link.
 */
export const fileLinkClick =
  (src: string | null | undefined) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    if (src && isFileLink(src) && tokenStore.needsRefresh()) {
      e.preventDefault();
      void openFile(src);
    }
  };
